import { useEffect, useRef } from 'react';
import { Download, CheckCircle2 } from 'lucide-react';
import useImageViewerStore from 'chat-ui/src/store/ImageViewerStore';
import { MessageAtachment } from '@common/ts/types';
import { cn } from '@/lib/utils';
import AudioMessage from './audio/AudioMessage';
import VideoMessage from './video/VideoMessage';
import { EMessageType } from '@/enums';
import { cleanCompletionLink } from '@/utils/cleanCompletionLink';
import useTranslation from '@/hooks/useTranslation';
import { formatMessageTimestamp } from '@/utils/dateFormat';

/** Convert markdown links and plain URLs to React anchor elements */
function linkifyText(text: string): React.ReactNode {
  if (!text) return null;

  // Combined regex: markdown links [text](url) OR plain URLs
  const combinedRegex =
    /\[([^\]]+)\]\(([^)]+)\)|(?:https?:\/\/|www\.)[^\s<]+(?:\.[^\s<]+)+(?:\/[^\s<]*)?/gi;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      // Markdown link [text](url)
      let href = match[2];
      if (!href.startsWith('http://') && !href.startsWith('https://')) {
        href = `https://${href}`;
      }
      parts.push(
        <a
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline hover:text-blue-700"
        >
          {match[1]}
        </a>,
      );
    } else {
      // Plain URL
      let href = match[0];
      if (!href.startsWith('http://') && !href.startsWith('https://')) {
        href = `https://${href}`;
      }
      parts.push(
        <a
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline hover:text-blue-700"
        >
          {match[0]}
        </a>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

interface ConversationMessageProps {
  _id: string;
  role: string;
  text: string;
  created_at: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  isHighlighted?: boolean;
}

function ConversationMessage({
  _id,
  role,
  data,
  text,
  created_at,
  isHighlighted,
}: ConversationMessageProps) {
  const { lang } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { setIsViewerOpen, setCurrentImageUrl, setCurrentImageAlt, setImages, setCurrentIndex } =
    useImageViewerStore();

  const isAssistant = role === 'assistant';
  const { cleanText, completionUrl } = cleanCompletionLink(text || '');

  // Scroll into view when highlighted (dual-timeout for layout shifts from lazy media)
  useEffect(() => {
    if (!isHighlighted || !ref.current) return undefined;

    const scrollToSelf = () => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const t1 = setTimeout(scrollToSelf, 350);
    const t2 = setTimeout(scrollToSelf, 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isHighlighted]);

  // --- Media helpers ---

  const audioItems =
    data?.filter((item: MessageAtachment) => item.type === EMessageType.audio) || [];
  const imageItems =
    data?.filter((item: MessageAtachment) => item.type === EMessageType.image) || [];
  const videoItems =
    data?.filter((item: MessageAtachment) => item.type === EMessageType.video) || [];
  const allMediaItems =
    data?.filter(
      (item: MessageAtachment) =>
        item.type === EMessageType.image || item.type === EMessageType.video,
    ) || [];

  const showImagePreview = (url: string, label: string) => {
    const images = allMediaItems.map((item: MessageAtachment) => ({
      url: item.url,
      label: item.label,
    }));
    setImages(images);
    const index = images.findIndex((img: { url: string }) => img.url === url);
    setCurrentIndex(index >= 0 ? index : 0);
    setCurrentImageUrl(url);
    setCurrentImageAlt(label || 'image preview');
    setIsViewerOpen(true);
  };

  const renderDownloadIcon = (item: MessageAtachment, downloadName: string) => (
    <a
      href={item.url}
      download={downloadName}
      className="inline-flex items-center text-neutral-400 hover:text-neutral-600 transition-colors"
      title="Download"
    >
      <Download className="h-3.5 w-3.5" />
    </a>
  );

  const renderMediaLabel = (item: MessageAtachment) =>
    item.label ? (
      <div className="w-full text-xs p-2 text-center text-gray-700 font-medium break-words">
        {item.label}
      </div>
    ) : null;

  return (
    <div
      ref={ref}
      id={`msg-${_id}`}
      className={cn(
        'flex flex-col max-w-[85%] transition-colors duration-500',
        isAssistant ? 'self-start' : 'self-end',
      )}
    >
      {/* Role label + timestamp */}
      <div className={cn('flex items-baseline gap-2 mb-1', !isAssistant && 'justify-end')}>
        <span
          className={cn(
            'text-[11px] font-semibold uppercase tracking-wide',
            isAssistant ? 'text-neutral-400' : 'text-primary',
          )}
        >
          {isAssistant ? lang.get('msg.interviewer') : lang.get('msg.respondent')}
        </span>
        {formatMessageTimestamp(created_at) && (
          <span className="text-xs text-neutral-500">{formatMessageTimestamp(created_at)}</span>
        )}
      </div>

      {/* Message card */}
      <div
        className={cn(
          'rounded-lg px-4 py-3',
          isAssistant
            ? 'bg-neutral-50 border border-neutral-200'
            : 'bg-primary/15 border border-primary/30',
          isHighlighted && 'ring-2 ring-primary/30',
        )}
      >
        <div className="min-w-0">
          {/* Text content */}
          {cleanText && (
            <p
              className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap break-words"
              dir="auto"
            >
              {linkifyText(cleanText)}
            </p>
          )}

          {/* Completion link */}
          {completionUrl && (
            <div className="mt-2 flex flex-col gap-1 rounded-md bg-primary/5 px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {lang.get('msg.completeInterview')}
              </span>
              <a
                href={completionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-primary hover:underline break-all"
              >
                {completionUrl}
              </a>
            </div>
          )}

          {/* Audio items */}
          {audioItems.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {audioItems.map((item: MessageAtachment, index: number) => {
                const itemKey = item.file_id || item.url || `audio-${index}`;
                return (
                  <div key={itemKey} className="rounded-lg overflow-hidden">
                    <div className="flex items-start gap-1.5">
                      <div className="flex-1 min-w-0">
                        <AudioMessage
                          isUser={role === 'user'}
                          url={item.url}
                          transcript={item.transcription_text || ''}
                        />
                      </div>
                      {renderDownloadIcon(item, 'tellet-audio')}
                    </div>
                    {renderMediaLabel(item)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Image items */}
          {imageItems.length > 0 && (
            <div
              className={cn(
                'mt-2 gap-2',
                // eslint-disable-next-line no-nested-ternary
                imageItems.length === 1
                  ? 'flex flex-col'
                  : imageItems.length === 2
                    ? 'grid grid-cols-2 max-w-md'
                    : 'grid grid-cols-3 max-w-lg',
              )}
            >
              {imageItems.map((item: MessageAtachment, index: number) => {
                const itemKey = item.file_id || item.url || `image-${index}`;
                return (
                  <div
                    key={itemKey}
                    className="flex flex-col rounded-lg overflow-hidden border border-neutral-200"
                  >
                    <div className="relative">
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                      <img
                        src={item.url}
                        alt={item.label || ''}
                        className="max-h-96 w-auto h-auto object-contain cursor-zoom-in hover:opacity-90 transition-opacity"
                        onClick={() => showImagePreview(item.url, item.label || '')}
                      />
                      <a
                        href={item.url}
                        download="tellet-image"
                        className="absolute top-2 right-2 p-1 rounded-full bg-white/80 text-neutral-400 hover:text-neutral-600 hover:bg-white transition-colors"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    </div>
                    {renderMediaLabel(item)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Video items */}
          {videoItems.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {videoItems.map((item: MessageAtachment, index: number) => {
                const itemKey = item.file_id || item.url || `video-${index}`;
                return (
                  <div key={itemKey} className="max-w-sm rounded-lg overflow-hidden">
                    <div className="flex items-start gap-1.5">
                      <div className="flex-1 min-w-0">
                        <VideoMessage url={item.url} transcript={item.transcription_text || ''} />
                      </div>
                      {renderDownloadIcon(item, 'tellet-video')}
                    </div>
                    {renderMediaLabel(item)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConversationMessage;
